import { useEffect, useState } from "react";
import { Bell, BellOff, Smartphone, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { enablePush, disablePush, getCurrentSubscription, pushSupported, isIOSNotInstalled } from "@/lib/push";
import { toast } from "sonner";

const PREF_GROUPS = [
  {
    title: "Clients",
    items: [
      ["comments", "Nouveaux commentaires"],
      ["modifications", "Demandes de modification"],
      ["validations", "Publications validées"],
      ["refusals", "Publications refusées"],
      ["new_requests", "Nouvelles demandes"],
      ["content_requests", "Contenus à fournir (demandes, réceptions, retards)"],
    ],
  },
  {
    title: "Planning",
    items: [
      ["planning_today", "Publications du jour"],
      ["planning_tomorrow", "Publications du lendemain"],
      ["planning_late", "Publications en retard / non programmées"],
      ["planning_pending", "Validations en attente"],
    ],
  },
  {
    title: "Rendez-vous & Prospection",
    items: [
      ["rdv", "RDV — nouvelles réservations en ligne, reports et annulations"],
      ["prospection", "Prospection — rappels et activité du pipeline"],
    ],
  },
  {
    title: "Temps de travail",
    items: [
      ["timer_long", "Chronomètre actif trop longtemps"],
      ["budget_80", "80 % du budget temps atteint"],
      ["budget_100", "100 % du budget temps atteint"],
      ["budget_over", "Budget temps dépassé"],
    ],
  },
  {
    title: "Rentabilité",
    items: [
      ["rentability_low", "Client sous le taux horaire cible"],
      ["rentability_drop", "Baisse importante de rentabilité"],
    ],
  },
  {
    title: "Pilotage global",
    items: [
      ["pilotage_charges", "Rappel de fin de mois — saisie des charges du mois en cours"],
    ],
  },
  {
    title: "Graphisme — Planification",
    items: [
      ["graphisme_planning", "Réservations provisoires (expiration), confirmations et replanification des projets"],
    ],
  },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${checked ? "bg-neutral-900" : "bg-neutral-200"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function NotificationSettings() {
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [devices, setDevices] = useState([]);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(false);
  const supported = pushSupported();

  const load = () => {
    getCurrentSubscription().then((s) => setSubscribed(!!s)).catch(() => {});
    if (typeof Notification !== "undefined") setPermission(Notification.permission);
    api.get("/push/devices").then((r) => setDevices(r.data)).catch(() => {});
    api.get("/push/prefs").then((r) => setPrefs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const enable = async () => {
    setLoading(true);
    try {
      await enablePush();
      toast.success("Notifications Push activées sur cet appareil");
      load();
    } catch (e) {
      setPermission(typeof Notification !== "undefined" ? Notification.permission : "default");
      toast.error(e.message === "permission-denied"
        ? "Permission refusée — autorisez les notifications dans les réglages du navigateur"
        : "Impossible d'activer les notifications Push");
    } finally { setLoading(false); }
  };

  const sendTest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) {
        toast.success(`Notification test envoyée à ${data.sent} appareil(s) — elle doit apparaître même application fermée`);
      } else {
        toast.error("Aucun appareil abonné — activez d'abord les notifications Push");
      }
    } catch { toast.error("Erreur lors de l'envoi du test"); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    setLoading(true);
    try {
      await disablePush();
      toast.success("Notifications Push désactivées sur cet appareil");
      load();
    } catch { toast.error("Erreur lors de la désactivation"); }
    finally { setLoading(false); }
  };

  const removeDevice = async (id) => {
    await api.delete(`/push/devices/${id}`);
    toast.success("Appareil supprimé");
    load();
  };

  const setPref = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    try { await api.patch("/push/prefs", { [key]: value }); }
    catch { toast.error("Erreur lors de l'enregistrement"); load(); }
  };

  const deviceLabel = (ua) => {
    if (/iPhone/i.test(ua)) return "iPhone";
    if (/iPad/i.test(ua)) return "iPad";
    if (/Android/i.test(ua)) return "Android";
    if (/Mac/i.test(ua)) return "Mac";
    if (/Windows/i.test(ua)) return "Windows";
    return "Appareil";
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-5" data-testid="notif-settings">
      <div>
        <h3 className="font-heading font-bold text-lg">Notifications</h3>
        <p className="text-sm text-neutral-500 mt-1">
          Recevez les événements importants (validations, demandes clients, rappels, alertes temps et rentabilité)
          directement sur vos appareils, même application fermée.
        </p>
      </div>

      {isIOSNotInstalled() && (
        <div className="text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-4 py-3" data-testid="push-ios-hint">
          Sur iPhone, les notifications Push nécessitent iOS 16.4+ et l'installation de l'app :
          dans Safari, touchez <strong>Partager</strong> puis <strong>« Sur l'écran d'accueil »</strong>,
          ouvrez DGA depuis son icône, puis revenez ici pour activer les notifications.
        </div>
      )}

      {(() => {
        const status = !supported
          ? { label: "Non compatible", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" }
          : permission === "denied"
            ? { label: "Permission refusée", cls: "bg-red-50 text-red-700 border-red-200" }
            : subscribed
              ? { label: "Activées", cls: "bg-green-50 text-green-700 border-green-200" }
              : { label: "Désactivées", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" };
        return (
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-xs font-medium border rounded-full px-2.5 py-1 ${status.cls}`} data-testid="push-state">
              État : {status.label}
            </span>
            {supported && !subscribed && (
              <Button data-testid="push-enable-btn" onClick={enable} disabled={loading}
                className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
                <Bell className="w-4 h-4" /> {loading ? "Activation…" : "Activer les notifications Push"}
              </Button>
            )}
            {subscribed && (
              <>
                <Button data-testid="push-test-btn" onClick={sendTest} disabled={loading}
                  className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
                  <Bell className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer une notification test"}
                </Button>
                <Button variant="outline" size="sm" data-testid="push-disable-btn" onClick={disable} disabled={loading} className="gap-1.5">
                  <BellOff className="w-3.5 h-3.5" /> Désactiver
                </Button>
              </>
            )}
            {permission === "denied" && (
              <p className="text-xs text-red-600 w-full" data-testid="push-denied-hint">
                Les notifications sont bloquées pour ce site. Sur iPhone : Réglages iOS → Notifications → DGA → autoriser,
                puis supprimez l'icône de l'écran d'accueil et rajoutez l'app.
              </p>
            )}
          </div>
        );
      })()}

      {devices.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">Appareils enregistrés</p>
          <div className="space-y-2">
            {devices.map((d) => (
              <div key={d.id} className="flex items-center gap-3 border border-neutral-200 rounded-lg px-3 py-2" data-testid={`push-device-${d.id}`}>
                <Smartphone className="w-4 h-4 text-neutral-400 shrink-0" />
                <span className="text-sm flex-1">{deviceLabel(d.user_agent)}</span>
                <button data-testid={`push-device-remove-${d.id}`} onClick={() => removeDevice(d.id)}
                  className="text-neutral-400 hover:text-red-600 transition-colors" title="Supprimer cet appareil">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {prefs && (
        <div className="space-y-5 pt-2 border-t border-neutral-100">
          {PREF_GROUPS.map((g) => (
            <div key={g.title}>
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">{g.title}</p>
              <div className="space-y-2">
                {g.items.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-3">
                    <span className="text-sm text-neutral-700">{label}</span>
                    <Toggle checked={!!prefs[key]} onChange={(v) => setPref(key, v)} testid={`pref-${key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-2">Résumé</p>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-neutral-700">Résumé quotidien (publications, validations, tâches du jour)</span>
              <Toggle checked={!!prefs.daily_summary} onChange={(v) => setPref("daily_summary", v)} testid="pref-daily-summary" />
            </div>
            {prefs.daily_summary && (
              <div className="grid grid-cols-2 gap-3 mt-3 animate-fade-up">
                <div>
                  <Label>Heure du résumé</Label>
                  <Input data-testid="daily-summary-time" type="time" className="mt-1.5" value={prefs.daily_summary_time || "08:30"}
                    onChange={(e) => setPref("daily_summary_time", e.target.value)} />
                </div>
                <div>
                  <Label>Fuseau horaire</Label>
                  <select data-testid="timezone-select" className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white"
                    value={prefs.timezone || "Europe/Paris"} onChange={(e) => setPref("timezone", e.target.value)}>
                    {["Europe/Paris", "Europe/Brussels", "Europe/Geneva", "America/Montreal", "Africa/Casablanca"].map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
